
import RcDomainObject from "@/domain/common/RcDomainObject.js";
import ChecklistItem from "@/domain/model/checklist/item/CheckListItem.js";
import ChecklistItemOptionMap from "@/domain/model/checklist/item/option/CheckListItemOptionMap.js";

//import CheckListItemMap from "./item/CheckListItemMap.js";
//import CheckListMap from "./CheckListMap.js";
import StringUtils from '@/utils/StringUtils.js'
import CheckListItemOption from "@/domain/model/checklist/item/option/CheckListItemOption.js";
import CheckListSnapshot from "@/domain/model/checklist/version/CheckListSnapshot.js";
import ImageMap from "@/domain/model/image/ImageMap.js";

export default class ChecklistItemResult extends RcDomainObject {
  
  static MODEL_NAME = 'CheckListItemResult';
  
  static Fields = {
      ITEM: ChecklistItem.MODEL_NAME,
      ITEM_OPTIONS: ChecklistItemOptionMap.MODEL_NAME,
      REPAIRED_OPTIONS: "Repaired" + ChecklistItemOptionMap.MODEL_NAME,
      RESULT_TYPE: "resultType",
      REMARKS: "remarks",
      VALUE: "value",
      ITEM_NAME: "itemName",
      PICKED_OPTIONS: "pickedOptions",
      MINOR_REMARKS: "minorRemarks",
      MAJOR_REMARKS: "majorRemarks",
      IMAGES: ImageMap.MODEL_NAME,
      // The answer to a Date and Time question: the instant picked, as epoch millis.
      DATE_VALUE: "dateValue",
      // The answer to an Employee question: links to the Employee records picked.
      EMPLOYEES: "EmployeeMap",
      // The answer to a Vehicle (Asset) question: links to the Vehicle records picked.
      VEHICLES: "VehicleMap",
  }
  
  static Values = {
      ResultTypes: {
        Initial: "initial",
        Enroute: "enroute",
      },
  }
  
  constructor(domain, data) {
    super(domain, data, ChecklistItemResult.MODEL_NAME);
  }

  fields() {
    return ChecklistItemResult.Fields;
  }
  
  find() {
    var result = this.domain.checklists().items().results().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    
    return result;
  }

  item() {
    var data = this.get(ChecklistItemResult.Fields.ITEM);
    return new ChecklistItem(this.domain, data);
  }
  
  withItem(item) {
    this.put(ChecklistItemResult.Fields.ITEM, item.data);
    return this;
  }

  options() {
    var data = this.get(ChecklistItemResult.Fields.ITEM_OPTIONS);
    return new ChecklistItemOptionMap(this.domain, data);
  }

  withOptions(options) {
    this.put(ChecklistItemResult.Fields.ITEM_OPTIONS, options.data);
    return this;
  }
  
  resultType() {
    return this.getString(ChecklistItemResult.Fields.RESULT_TYPE);
  }
  
  value() {
    return this.getString(ChecklistItemResult.Fields.VALUE);
  }
  
  remarks() {
    return this.getString(ChecklistItemResult.Fields.REMARKS);
  }

  /** A Date and Time answer, in epoch millis; 0 when there is none. */
  dateValue() {
    const value = Number(this.get(ChecklistItemResult.Fields.DATE_VALUE));
    return Number.isFinite(value) ? value : 0;
  }
  
  /** The employees picked as the answer to an Employee question, each once, in the order held. */
  employeeIds() {
    return this._linkIds(ChecklistItemResult.Fields.EMPLOYEES);
  }

  /** The vehicles picked as the answer to a Vehicle (Asset) question, each once, in the order held. */
  vehicleIds() {
    return this._linkIds(ChecklistItemResult.Fields.VEHICLES);
  }

  // The ids in a link set, read as storage hands it back (an array of ids or links) or as a
  // save sends it (an object keyed by id).
  _linkIds(field) {
    var data = this.get(field);
    var ids = [];
    var add = (id) => {
      if (typeof id === 'string' && id.startsWith('#') && !ids.includes(id)) {
        ids.push(id);
      }
    };
    if (Array.isArray(data)) {
      data.forEach(entry => add(typeof entry === 'string' ? entry : entry?.['@rid']));
    } else if (data != null && typeof data === 'object') {
      Object.keys(data).forEach(add);
    }
    return ids;
  }

  hasRemarks() {
    return !StringUtils.isEmpty(this.remarks());
  }
  
  // What the driver wrote about the minor defects picked, kept apart from the major
  // ones because a schedule prints them in separate columns.
  minorRemarks() {
    return this.getString(ChecklistItemResult.Fields.MINOR_REMARKS);
  }
  
  withMinorRemarks(minorRemarks) {
    this.put(ChecklistItemResult.Fields.MINOR_REMARKS, minorRemarks);
    return this;
  }
  
  hasMinorRemarks() {
    return !StringUtils.isEmpty(this.minorRemarks());
  }
  
  majorRemarks() {
    return this.getString(ChecklistItemResult.Fields.MAJOR_REMARKS);
  }
  
  withMajorRemarks(majorRemarks) {
    this.put(ChecklistItemResult.Fields.MAJOR_REMARKS, majorRemarks);
    return this;
  }
  
  hasMajorRemarks() {
    return !StringUtils.isEmpty(this.majorRemarks());
  }
  
  // The photos taken against this answer.
  images() {
    var data = this.get(ChecklistItemResult.Fields.IMAGES);
    return new ImageMap(this.domain, data);
  }
  
  withImages(images) {
    this.put(ChecklistItemResult.Fields.IMAGES, images.data);
    return this;
  }
  
  hasImages() {
    return this.images().size() > 0;
  }

  // Most photos an answer carries (contract §10).
  static MAX_PHOTOS = 4;

  // The answer's photos to show, in index order, at most four: its ImageMap ids resolved
  // against the Image records the inspection reply carries in its flat ImageMap. An id with
  // no record behind it, or a record with nothing to show, is left out.
  photos() {
    return this.images().find().sortByIndex()
      .filter(image => image.hasUrl() || image.image().length > 0)
      .slice(0, ChecklistItemResult.MAX_PHOTOS);
  }

  hasPhotos() {
    return this.photos().length > 0;
  }

  // Takes the photo list off the answer before it is sent back unchanged. Without the key
  // the server keeps the stored photos; sending the list would only risk an outdated one.
  withoutPhotoList() {
    delete this.data[ChecklistItemResult.Fields.IMAGES];
    return this;
  }
  
  isInitial() {
    return StringUtils.eic(ChecklistItemResult.Values.ResultTypes.Initial, this.resultType());
  }

  hasResultType() {
    return !StringUtils.isEmpty(this.resultType());
  }
  
  isEnroute() {
    // Compared without regard to case, matching isInitial above - the pair decides which
    // pass a result belongs to and they have to agree on how they read the stored value.
    return StringUtils.eic(ChecklistItemResult.Values.ResultTypes.Enroute, this.resultType());
  }
  
  pointer() {
    var data = {'@rid': this.id()};
    var result = new ChecklistItemResult(this.domain, data);
    return result;
  }
  
  copy() {
    var data = this._copy();
    return new ChecklistItemResult(this.domain, data);
  }

  // The question's name as it read when this answer was given, or null when the answer
  // was saved without one (before frozen copies existed).
  itemName() {
    if (!this.has(ChecklistItemResult.Fields.ITEM_NAME)) {
      return null;
    }
    return this.getString(ChecklistItemResult.Fields.ITEM_NAME);
  }

  // The choices picked, as they read when the answer was given - name, place and
  // severity - sorted by place. An empty list for a typed-in answer; null when the
  // answer was saved without them, or they cannot be read.
  pickedOptions() {
    if (!this.has(ChecklistItemResult.Fields.PICKED_OPTIONS)) {
      return null;
    }
    let raw = this.get(ChecklistItemResult.Fields.PICKED_OPTIONS);
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }
    if (!Array.isArray(raw)) {
      return null;
    }
    return raw
      .filter(entry => entry != null && typeof entry === 'object')
      .map(entry => CheckListSnapshot.toOption(this.domain, entry))
      .sort((a, b) => a.index() - b.index());
  }

  // The choices this answer picked, to show under its question. Read from the copy
  // taken with the answer when there is one, then from the frozen checklist, and only
  // then from the live choices - which may since have been renamed or removed.
  optionsAsAnswered(snapshot) {
    const picked = this.pickedOptions();
    if (picked != null) {
      return picked;
    }
    const live = this.options().find();
    if (snapshot == null) {
      return live.sortByIndex();
    }
    const itemId = this.item().id();
    return this.options().keys()
      .map(optionId => snapshot.option(itemId, optionId) || live.getCheckListItemOption(optionId))
      .filter(option => option.isNotNew())
      .sort((a, b) => a.index() - b.index());
  }

  // The choices this answer to a Rollup's nested question picked. A nested question's
  // choices are not in the stores - they are held on the Rollup - so after the answer's own
  // copy and the frozen checklist, they are read from `question`, the nested question as
  // the Rollup holds it, and last from what the answer itself carries.
  nestedOptionsAsAnswered(question, snapshot) {
    const picked = this.pickedOptions();
    if (picked != null) {
      return picked;
    }
    const itemId = this.item().id();
    const held = this.options();
    return held.keys()
      .map(optionId => {
        const frozen = snapshot != null ? snapshot.option(itemId, optionId) : null;
        if (frozen != null) {
          return frozen;
        }
        if (question.options().has(optionId)) {
          return question.options().getCheckListItemOption(optionId);
        }
        return held.getCheckListItemOption(optionId);
      })
      .filter(option => option.isNotNew())
      .sort((a, b) => a.index() - b.index());
  }

  // Before an answer is sent back to the server (a repair re-saves it): an answer whose
  // choices arrived empty while its own copy of them (pickedOptions) names some gets them back
  // as pointers, so the re-save cannot wipe the picks. Seen on Rollup nested answers, whose
  // choices are not in the stores. Anything that already holds choices is left alone.
  restorePickedOptions() {
    if (this.options().size() > 0) {
      return this;
    }
    const picked = this.pickedOptions();
    if (picked == null || picked.length === 0) {
      return this;
    }
    const map = new ChecklistItemOptionMap(this.domain, {});
    picked.forEach(option => {
      map.add(new CheckListItemOption(this.domain, {
        '@rid': option.id(),
        '@class': CheckListItemOption.MODEL_NAME,
      }));
    });
    return this.withOptions(map);
  }

  // The name a repaired choice had when it was picked, falling back to its live name.
  optionNameAsAnswered(optionId) {
    const picked = this.pickedOptions();
    if (picked != null) {
      const found = picked.find(option => option.id() === optionId);
      if (found) {
        return found.name();
      }
    }
    return new CheckListItemOption(this.domain, optionId).find().name();
  }

  repairedItemOptions() {
    return new ChecklistItemOptionMap(this.domain, this.get(ChecklistItemResult.Fields.REPAIRED_OPTIONS));
  }

  isRepaired(itemOption) {
    // Asks whether the option is held, rather than whether the one we get back looks
    // saved: isNew() answers true for any option that has not been persisted, so a
    // repair recorded against an unsaved option never read back as repaired.
    return this.repairedItemOptions().has(itemOption.id());
  }
  
  addRepairedItemOption(itemOption) {
    let repairedOptions = this.repairedItemOptions();
    repairedOptions.add(itemOption);
    return this.withRepairedItemOptions(repairedOptions);
  }

  withRepairedItemOptions(repairedOptions) {
    this.put(ChecklistItemResult.Fields.REPAIRED_OPTIONS, repairedOptions.data);
    return this;
  }

  // Resolves the answer's question and choices from the stores for editing and re-saving.
  // A choice the store does not hold is kept as the pointer it was, never dropped: find()
  // leaves such a choice out, and a repair re-saving the answer would then send it with its
  // picks or repairs gone (a Rollup's nested answers before the server put their options in
  // the flat map). A nested question is not in the item store either; find() then hands the
  // pointer back, which is all a re-save needs - its name and choices are read from the
  // Rollup's own map and the answer's copies, never from here.
  load() {
    this.withItem(this.item().find());
    this.withOptions(ChecklistItemResult._findKeepingUnknown(this.options()));
    this.withRepairedItemOptions(ChecklistItemResult._findKeepingUnknown(this.repairedItemOptions()));
  }

  static _findKeepingUnknown(held) {
    const found = held.find();
    held.keys().forEach(key => {
      if (!found.has(key)) {
        found.add(held.getCheckListItemOption(key));
      }
    });
    return found;
  }
}